import React, { useState, useEffect } from 'react';
import { 
  UsersIcon, 
  ChartBarIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  PauseIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { AdminHeader } from '../../../shared/components';

interface DashboardData {
  overview: {
    totalUsers: number;
    newUsersThisMonth: number;
    subscriptionBreakdown: Array<{
      _id: string;
      count: number;
      totalUsedLinks: number;
      totalUsedClicks: number;
    }>;
  };
  alerts: {
    expiringSoon: number;
    expiringSoonList: Array<{
      email: string;
      username: string;
      plan: string;
      planExpires: string;
    }>;
    highUsage: number;
    highUsageList: Array<{
      email: string;
      username: string;
      plan: string;
      usageStatus: {
        linksUsed: number;
        linksLimit: number;
        clicksUsed: number;
        clicksLimit: number;
        usagePercentage: number;
      };
    }>;
  };
  systemStatus: {
    lastReset: any;
    schedulerStatus: {
      isRunning: boolean;
      nextExecution: string;
    };
  };
}

// 대시보드 카드 컴포넌트
const DashboardCard: React.FC<{
  title: string;
  value: string;
  icon: React.ReactNode;
  bgColor: string;
}> = ({ title, value, icon, bgColor }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
    <div className="flex items-center">
      <div className={`flex-shrink-0 p-3 rounded-md bg-${bgColor}-100 dark:bg-${bgColor}-900`}>
        {icon}
      </div>
      <div className="ml-4">
        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
          {title}
        </h3>
        <p className="text-2xl font-semibold text-gray-900 dark:text-white">
          {value}
        </p>
      </div>
    </div>
  </div>
);

const SubscriptionManagementPage: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      console.log('📊 Fetching subscription dashboard data...');
      const response = await fetch('/api/admin/subscription/dashboard');
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('📊 Subscription dashboard response:', data);
      
      if (data.success) {
        setDashboardData(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch subscription data');
      }
    } catch (error) {
      console.error('대시보드 데이터 로드 실패:', error);
      toast.error('구독 데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 사용량 초기화 핸들러
  const handleResetUsage = async () => {
    if (!confirm('모든 사용자의 월별 사용량을 초기화하시겠습니까?')) {
      return;
    }

    try {
      console.log('🔄 Starting manual usage reset...');
      const response = await fetch('/api/admin/subscription/reset-usage', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      console.log('🔄 Reset response:', data);
      
      if (data.success) {
        alert(`${data.message}`);
        fetchDashboardData();
      } else {
        alert(`초기화 실패: ${data.message}`);
      }
    } catch (error) {
      console.error('Reset error:', error);
      alert('초기화 중 오류가 발생했습니다');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
      <AdminHeader title="구독 관리" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            구독 관리
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            사용자 구독 상태 및 월별 사용량을 관리합니다
          </p>
      </div>

      {/* 대시보드 개요 카드 */}
      {dashboardData && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <DashboardCard
              title="전체 사용자"
              value={(dashboardData.overview.totalUsers || 0).toLocaleString()}
              icon={<UsersIcon className="w-8 h-8 text-blue-500" />}
              bgColor="blue"
            />
            <DashboardCard
              title="신규 사용자 (이번 달)"
              value={(dashboardData.overview.newUsersThisMonth || 0).toLocaleString()}
              icon={<ChartBarIcon className="w-8 h-8 text-green-500" />}
              bgColor="green"
            />
            <DashboardCard
              title="만료 예정"
              value={(dashboardData.alerts?.expiringSoon || 0).toString()}
              icon={<ClockIcon className="w-8 h-8 text-yellow-500" />}
              bgColor="yellow"
            />
            <DashboardCard
              title="고사용량 사용자"
              value={(dashboardData.alerts?.highUsage || 0).toString()}
              icon={<ExclamationTriangleIcon className="w-8 h-8 text-red-500" />}
              bgColor="red"
            />
          </div>

          {/* 구독 플랜별 분석 & 사용량 초기화 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* 구독 분석 카드 */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                플랜별 사용량 분석
              </h3>
              <div className="space-y-4">
                {(dashboardData.overview.subscriptionBreakdown || []).map((plan) => (
                  <div key={plan._id} className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                    <div>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {plan._id === 'unknown' ? 'Unknown Users (비회원)' : plan._id}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">
                        {plan._id === 'unknown' ? '(비회원 생성)' : `(${(plan.count || 0)}명)`}
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-900 dark:text-white">
                        링크: {(plan.totalUsedLinks || 0).toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        클릭: {(plan.totalUsedClicks || 0).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 사용량 초기화 카드 */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                사용량 초기화 관리
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">스케줄러 상태</span>
                  <div className="flex items-center">
                    {dashboardData.systemStatus?.schedulerStatus?.isRunning ? (
                      <PlayIcon className="w-4 h-4 text-green-500 mr-1" />
                    ) : (
                      <PauseIcon className="w-4 h-4 text-red-500 mr-1" />
                    )}
                    <span className={`text-sm ${dashboardData.systemStatus?.schedulerStatus?.isRunning ? 'text-green-600' : 'text-red-600'}`}>
                      {dashboardData.systemStatus?.schedulerStatus?.isRunning ? '실행 중' : '중지됨'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">다음 실행</span>
                  <span className="text-sm text-gray-900 dark:text-white">
                    {dashboardData.systemStatus?.schedulerStatus?.nextExecution || '없음'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">마지막 초기화</span>
                  <span className="text-sm text-gray-900 dark:text-white">
                    {dashboardData.systemStatus?.lastReset ? 
                      new Date(dashboardData.systemStatus.lastReset.timestamp).toLocaleDateString() : 
                      '없음'
                    }
                  </span>
                </div>
                <button
                  onClick={handleResetUsage}
                  className="w-full bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors flex items-center justify-center"
                >
                  <ArrowPathIcon className="w-4 h-4 mr-2" />
                  수동 초기화 실행
                </button>
              </div>
            </div>
          </div>

          {/* 알림 목록 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* 만료 예정 사용자 */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                만료 예정 사용자 ({dashboardData.alerts?.expiringSoon || 0})
              </h3>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {(dashboardData.alerts?.expiringSoonList || []).map((user, index) => (
                  <div key={index} className="flex justify-between items-center p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-md">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">{user.username}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{user.email}</div>
                      <div className="text-sm text-yellow-600 dark:text-yellow-400">{user.plan}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {new Date(user.planExpires).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))}
                {(!dashboardData.alerts?.expiringSoonList || dashboardData.alerts.expiringSoonList.length === 0) && (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                    만료 예정 사용자가 없습니다
                  </p>
                )}
              </div>
            </div>

            {/* 고사용량 사용자 */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                고사용량 사용자 ({dashboardData.alerts?.highUsage || 0})
              </h3>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {(dashboardData.alerts?.highUsageList || []).map((user, index) => (
                  <div key={index} className="flex justify-between items-center p-3 bg-red-50 dark:bg-red-900/20 rounded-md">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">{user.username}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{user.email}</div>
                      <div className="text-sm text-red-600 dark:text-red-400">{user.plan}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-red-600 dark:text-red-400">
                        {(user.usageStatus?.usagePercentage || 0).toFixed(1)}%
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {user.usageStatus?.linksUsed || 0}/{user.usageStatus?.linksLimit || 0}
                      </div>
                    </div>
                  </div>
                ))}
                {(!dashboardData.alerts?.highUsageList || dashboardData.alerts.highUsageList.length === 0) && (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                    고사용량 사용자가 없습니다
                  </p>
                )}
              </div>
            </div>
          </div>
        </>
      )}
      </div>
    </>
  );
};

export default SubscriptionManagementPage;